/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronUp, ChevronDown, X, ChevronLeft, ChevronRight, Palette, Clock, CheckCircle, Plus, MessageSquare } from 'lucide-react';
import PhonePreview, { industryConfig, typeConfig } from './PhonePreview';
import apiClient from '../api/client';
import { validateTextField } from '../utils/validation';
import { FIELD_LIMITS, DAISY_THEMES } from '../utils/constants';
import { INDUSTRIES, IDENTIFIER_FORMATS } from '../constants/workflowOptions';
import { SUPPORTED_FILE_FORMATS } from '../constants/mimeMap';
import { useAuth } from '../context/AuthContext';
import { SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const TIME_UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' }
];

const ERR_EXPIRY_MINUTES = 'Expiry must be at least 15 minutes';
const ERR_EXPIRY_POSITIVE = 'Please enter a valid positive number';

const BASE_SCREEN_COUNT = 3;

const MIN_MINUTES_EXPIRY = 15;

const HOURS_TO_MILLISECONDS = 3600000;

// Shared UI literals (Sonar: S1192)
const CLASS_READONLY_DISABLED = 'bg-base-200 cursor-not-allowed disabled:opacity-60';
const CLASS_INPUT_ERROR = 'border-error ring-1 ring-error/20';
const CLASS_SELECTION_ACTIVE = 'border-primary bg-primary/10 shadow-sm';
const CLASS_SELECTION_INACTIVE = 'border-base-300 bg-base-100 hover:border-primary/30 hover:bg-base-200';
const CLASS_INPUT_BASE = 'w-full px-4 py-3 border rounded-xl bg-base-100 text-base-content placeholder-base-content/30 focus:ring-2 focus:ring-primary focus:border-primary outline-none';
const CLASS_BRANDING_INPUT_BASE = 'w-full px-3 py-2.5 border rounded-lg text-sm bg-base-100 text-base-content focus:ring-2 focus:ring-primary focus:border-primary outline-none';
const CLASS_BORDER_DEFAULT = 'border-base-300';
const CLASS_TOGGLE_TRACK = 'relative inline-flex h-[18px] w-8 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none';
const CLASS_TOGGLE_THUMB = 'pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out';

const formatExpiryLabel = (value, unit) => {
  if (!value || !unit) { return '—'; }

  switch (unit) {
    case 'minutes':
      return `${value} minute${value !== 1 ? 's' : ''}`;
    case 'hours':
      return `${value} hour${value !== 1 ? 's' : ''}`;
    case 'days':
      return `${value} day${value !== 1 ? 's' : ''}`;
    default:
      return `${value} ${unit}`;
  }
};

const convertToHours = (value, unit) => {
  switch (unit) {
    case 'minutes':
      return value / 60;
    case 'hours':
      return value;
    case 'days':
      return value * 24;
    default:
      return value;
  }
};

const WorkflowBuilder = ({ workflow, onSave, onCancel, readOnly = false }) => {
  const { user } = useAuth();

  const [name, setName] = useState(workflow?.name || '');
  const [industry, setIndustry] = useState(workflow?.industry || '');
  const [description, setDescription] = useState(workflow?.description || '');
  const [customerReferenceLabel, setCustomerReferenceLabel] = useState(workflow?.customerReferenceLabel || '');

  const isLocked = !!workflow?.inUse;

  const [items, setItems] = useState(() => {
    const initialItems = workflow?.items?.map(i => ({
      ...i,
      tempId: i.id || Date.now() + Math.random(),
      audioRequired: i.audioRequired || false,
      allowedFileType: (i.allowedFileType ?? i.allowed_file_type)?.toUpperCase() ?? null,
    })) || [];

    // Ensure if only one step, it's required
    if (initialItems.length === 1) {
      initialItems[0].required = true;
    }

    return initialItems;
  });
  const [branding, setBranding] = useState({
    theme: (!workflow?.id && user?.subAccountWorkflowTheme) ? user.subAccountWorkflowTheme : (workflow?.branding?.theme || 'light'),
    logoUrl: (!workflow?.id && user?.subAccountWorkflowLogoUrl) ? user.subAccountWorkflowLogoUrl : (workflow?.branding?.logoUrl || ''),
    welcomeTitle: workflow?.branding?.welcomeTitle || 'Welcome, {name}',
    welcomeSubtitle: workflow?.branding?.welcomeSubtitle || '',
    instructionsMainTitle: workflow?.branding?.instructionsMainTitle || '',
    instructionsHeading: workflow?.branding?.instructionsHeading || '',
    thankyouTitle: workflow?.branding?.thankyouTitle || '',
    thankyouMessage: workflow?.branding?.thankyouMessage || '',
    whatHappensNextDescription: workflow?.branding?.whatHappensNextDescription || '',
  });

  // New expiry system state
  const [customValue, setCustomValue] = useState(workflow?.defaultExpiry || '24');
  const [customUnit, setCustomUnit] = useState(workflow?.expiryTimeUnit || 'hours');
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [captureProvenanceDetails, setCaptureProvenanceDetails] = useState(workflow?.captureProvenanceDetails || false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ name: '', industry: '', customerReferenceLabel: '', steps: '', items: {} });
  const [brandingErrors, setBrandingErrors] = useState({});
  const [previewStep, setPreviewStep] = useState(0);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [recipientMessagingOpen, setRecipientMessagingOpen] = useState(false);
  const [lastAddedId, setLastAddedId] = useState(null);

  // Load expiry settings on component mount
  useEffect(() => {
    const loadExpiryAndProvenanceSettings = async () => {
      setLoadingSettings(true);
      try {
        // Load tenant settings first
        const response = await apiClient.get('/settings');
        const settings = response.data;

        // If editing workflow, use workflow settings
        if (workflow?.defaultExpiry && workflow?.expiryTimeUnit) {
          setCustomValue(String(workflow.defaultExpiry));
          setCustomUnit(workflow.expiryTimeUnit);
        } else if (workflow?.defaultExpiryHours) {
          // Backwards compatibility for old workflows
          setCustomValue(String(workflow.defaultExpiryHours));
          setCustomUnit('hours');
        } else {
          // Load from tenant settings (for new workflows)
          if (settings.defaultExpiry && settings.expiryTimeUnit) {
            setCustomValue(String(settings.defaultExpiry));
            setCustomUnit(settings.expiryTimeUnit);
          } else if (settings.defaultExpiryHours) {
            // Backwards compatibility for old tenant settings
            setCustomValue(String(settings.defaultExpiryHours));
            setCustomUnit('hours');
          } else {
            // Default fallback
            setCustomValue('24');
            setCustomUnit('hours');
          }
        }

        // Handle provenance settings - inherit from tenant by default
        if (workflow?.captureProvenanceDetails !== undefined) {
          // Editing workflow - use workflow's setting
          setCaptureProvenanceDetails(workflow.captureProvenanceDetails);
        } else {
          // New workflow - inherit from tenant settings
          setCaptureProvenanceDetails(settings.captureProvenanceDetails || false);
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
        // Default fallback on error
        // setCustomValue('24');
        // setCustomUnit('hours');
        // setCaptureProvenanceDetails(false);
      } finally {
        setLoadingSettings(false);
      }
    };

    loadExpiryAndProvenanceSettings();
  }, [workflow]);

  const updateBranding = (field, value) => setBranding(prev => ({ ...prev, [field]: value }));

  const validateBrandingField = (field, value, label, maxLength) => {
    const error = validateTextField(value, label, { required: false, minLength: null, maxLength });
    setBrandingErrors(prev => ({ ...prev, [field]: error }));
  };

  const totalScreens = BASE_SCREEN_COUNT + items.length; // welcome + instructions + steps + done

  const addItem = () => {
    const newId = Date.now() + Math.random();
    setItems(prev => [
      ...prev,
      {
        tempId: newId,
        type: 'photo',
        description: '',
        required: true,
        identifierFormat: null,
        stepOrder: prev.length + 1,
        audioRequired: false,
        allowedFileType: null
      }
    ]);
    setLastAddedId(newId);
    setPreviewStep(2 + items.length);

    // Clear error messages if user adds a step
    setFieldErrors(prev => {
      const updatedItemsErrors = { ...prev.items };
      Object.keys(updatedItemsErrors).forEach(tempId => {
        if (updatedItemsErrors[tempId]?.required) {
          updatedItemsErrors[tempId] = { ...updatedItemsErrors[tempId], required: '' };
        }
      });
      return { ...prev, steps: '', items: updatedItemsErrors };
    });
  };

  const removeItem = (tempId) => {
    setItems(prev => {
      const filtered = prev.filter(i => i.tempId !== tempId);
      // Ensure at least one item remains required if any items left
      if (filtered.length > 0 && !filtered.some(i => i.required)) {
        filtered[0].required = true;
      }
      return filtered.map((i, idx) => ({ ...i, stepOrder: idx + 1 }));
    });
  };

  const updateItem = (tempId, field, value) => {
    setItems(prev => prev.map(i => i.tempId === tempId ? { ...i, [field]: value } : i));
  };

  const moveItem = (tempId, dir) => {
    setItems(prev => {
      const idx = prev.findIndex(i => i.tempId === tempId);
      if ((dir === -1 && idx === 0) || (dir === 1 && idx === prev.length - 1)) { return prev; }
      const newItems = [...prev];
      [newItems[idx], newItems[idx + dir]] = [newItems[idx + dir], newItems[idx]];
      return newItems.map((i, index) => ({ ...i, stepOrder: index + 1 }));
    });
  };

  const validateName = () => {
    const error = validateTextField(name, 'Workflow name', {
      required: true,
      minLength: FIELD_LIMITS.MIN_LENGTH,
      maxLength: FIELD_LIMITS.DEFAULT_TEXT,
    });
    setFieldErrors(prev => ({ ...prev, name: error }));
  };

  const validateItem = (tempId, field, value, type) => {
    let errorMsg = '';
    if (field === 'description') {
      errorMsg = validateTextField(value, 'Capture step details', {
        required: true,
        minLength: null,
        maxLength: FIELD_LIMITS.DEFAULT_TEXT,
      });
    } else if (field === 'identifierFormat') {
      if (type === 'identifier' && !value) { errorMsg = 'Format is required'; }
    } else {
      errorMsg = 'Unknown field';
    }

    setFieldErrors(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [tempId]: {
          ...(prev.items[tempId] || {}),
          [field]: errorMsg
        }
      }
    }));
  };

  const validateExpiry = (value, unit) => {
    let expiryError = '';
    const val = parseInt(value, 10);
    if (!value) {
      expiryError = ''; // Don't show error while typing if empty
    } else if (isNaN(val) || val <= 0) {
      expiryError = ERR_EXPIRY_POSITIVE;
    } else if (unit === 'minutes' && val < MIN_MINUTES_EXPIRY) {
      expiryError = ERR_EXPIRY_MINUTES;
    } else {
      expiryError = '';
    }
    setFieldErrors(prev => ({ ...prev, expiry: expiryError }));
    return !expiryError;
  };

  const handleSave = async () => {
    let hasErrors = false;

    const newNameError = validateTextField(name, 'Workflow name', {
      required: true,
      minLength: FIELD_LIMITS.MIN_LENGTH,
      maxLength: FIELD_LIMITS.DEFAULT_TEXT,
    });
    if (newNameError) { hasErrors = true; }

    const newDescriptionError = validateTextField(description, 'Workflow description', {
      required: false,
      minLength: null,
      maxLength: FIELD_LIMITS.DESCRIPTION,
    });
    if (newDescriptionError) { hasErrors = true; }

    const newCustomerReferenceLabelError = validateTextField(customerReferenceLabel, 'Customer reference label', {
      required: false,
      minLength: null,
      maxLength: FIELD_LIMITS.MAX_CUSTOMER_REFERENCE_LABEL_LENGTH,
    });
    if (newCustomerReferenceLabelError) { hasErrors = true; }

    const newItemsErrors = {};
    items.forEach(i => {
      newItemsErrors[i.tempId] = {};
      const descError = validateTextField(i.description, 'Capture step details', {
        required: true,
        minLength: null,
        maxLength: FIELD_LIMITS.DEFAULT_TEXT,
      });
      if (descError) {
        newItemsErrors[i.tempId].description = descError;
        hasErrors = true;
      } else {
        newItemsErrors[i.tempId].description = '';
        hasErrors = hasErrors || false;
      }
      if (i.type === 'identifier' && !i.identifierFormat) {
        newItemsErrors[i.tempId].identifierFormat = 'Format is required';
        hasErrors = true;
      }
      if (i.type === 'file' && !i.allowedFileType) {
        newItemsErrors[i.tempId].allowedFileType = 'Format selection is required for File steps';
        hasErrors = true;
      }
    });

    let industryError = '';
    if (!industry) {
      industryError = 'Industry is required. Please select an industry.';
      hasErrors = true;
    }

    let stepsError = '';
    if (items.length === 0) {
      stepsError = 'At least one capture step is required.';
      hasErrors = true;
    } else if (!items.some(i => i.required)) {
      stepsError = 'At least one step must be marked as required.';
      hasErrors = true;
    } else {
      stepsError = '';
      hasErrors = hasErrors || false;
    }

    let expiryError = '';
    const val = parseInt(customValue, 10);
    if (!customValue) {
      expiryError = 'Please enter a value';
      hasErrors = true;
    } else if (isNaN(val) || val <= 0) {
      expiryError = ERR_EXPIRY_POSITIVE;
      hasErrors = true;
    } else if (customUnit === 'minutes' && val < MIN_MINUTES_EXPIRY) {
      expiryError = ERR_EXPIRY_MINUTES;
      hasErrors = true;
    } else {
      expiryError = '';
      hasErrors = hasErrors || false;
    }

    const brandingValidations = [
      { field: 'welcomeTitle', label: 'Welcome title', max: FIELD_LIMITS.DEFAULT_TEXT },
      { field: 'welcomeSubtitle', label: 'Welcome subtitle', max: FIELD_LIMITS.DESCRIPTION },
      { field: 'instructionsMainTitle', label: 'Instructions main title', max: FIELD_LIMITS.DEFAULT_TEXT },
      { field: 'instructionsHeading', label: 'Instructions heading', max: FIELD_LIMITS.DESCRIPTION },
      { field: 'thankyouTitle', label: 'Thank you title', max: FIELD_LIMITS.DEFAULT_TEXT },
      { field: 'thankyouMessage', label: 'Thank you message', max: FIELD_LIMITS.DESCRIPTION },
      { field: 'whatHappensNextDescription', label: 'What happens next description', max: FIELD_LIMITS.DESCRIPTION },
    ];
    const newBrandingErrors = {};
    brandingValidations.forEach(({ field, label, max }) => {
      const err = validateTextField(branding[field], label, { required: false, minLength: null, maxLength: max });
      if (err) { newBrandingErrors[field] = err; hasErrors = true; }
    });
    setBrandingErrors(newBrandingErrors);

    setFieldErrors({
      name: newNameError,
      description: newDescriptionError,
      industry: industryError,
      customerReferenceLabel: newCustomerReferenceLabelError,
      steps: stepsError,
      expiry: expiryError,
      items: newItemsErrors
    });

    if (hasErrors) {
      return;
    }

    setError('');
    setSaving(true);
    try {
      const effectiveExpiry = parseInt(customValue, 10) || null;
      await onSave({
        name,
        industry,
        description,
        items: items.map(i => ({
          ...(i.id ? { id: i.id } : {}),
          type: i.type,
          description: i.description,
          stepOrder: i.stepOrder,
          required: i.required,
          identifierFormat: i.identifierFormat ?? null,
          audioRequired: i.audioRequired || false,
          allowedFileType: i.type === 'file' ? (i.allowedFileType ? i.allowedFileType.toLowerCase() : null) : null
        })),
        customerReferenceLabel: customerReferenceLabel.trim() || undefined,
        branding,
        defaultExpiry: isNaN(effectiveExpiry) ? null : effectiveExpiry,
        expiryTimeUnit: customUnit,
        captureProvenanceDetails: captureProvenanceDetails
      });
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg[0] : (msg || 'Failed to save workflow'));
    } finally {
      setSaving(false);
    }
  };

  const screenLabels = ['Welcome', 'Steps', ...items.map((_, i) => `#${i + 1}`), 'Done'];

  // Calculate the expiry date
  const expiresAtDate = new Date(
    Date.now() +
    convertToHours(parseInt(customValue, 10), customUnit) * HOURS_TO_MILLISECONDS
  );

  return (
    <div className="flex flex-col lg:flex-row h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-xl border border-base-300 shadow-sm bg-base-100">
      {/* Left: Editor */}
      <div className="flex-1 overflow-y-auto border-b lg:border-b-0 lg:border-r border-base-300 bg-base-100/50 backdrop-blur-sm">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-base-100 border-b border-base-300 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-base-content">{readOnly ? 'View Workflow' : (workflow ? 'Edit Workflow' : 'New Capture Workflow')}</h2>
            <p className="text-xs text-base-content/60 mt-0.5">{readOnly ? 'Review workflow configuration' : 'Build your capture flow — preview updates live'}</p>
          </div>
          <button onClick={onCancel} className="p-2 text-base-content/40 hover:text-base-content hover:bg-base-200 rounded-lg transition">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Workflow Name */}
          <div>
            <label htmlFor="workflow-name" className="block text-sm font-semibold text-base-content/70 mb-2">Workflow Name *</label>
            <input
              id="workflow-name"
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value);
                if (fieldErrors.name) { setFieldErrors(prev => ({ ...prev, name: '' })); }
              }}
              onBlur={validateName}
              onFocus={() => setPreviewStep(0)}
              disabled={isLocked || readOnly}
              placeholder="e.g. Vehicle Damage Inspection"
              className={`${CLASS_INPUT_BASE} text-sm disabled:opacity-60 transition-all ${fieldErrors.name ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
            />
            {fieldErrors.name && (
              <p className="text-error text-xs mt-1 font-medium">
                {fieldErrors.name}
              </p>
            )}
          </div>

          {/* Industry Selection */}
          <div>
            <span className="block text-sm font-semibold text-base-content/70 mb-2.5">Industry *</span>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
              {INDUSTRIES.map(ind => {
                const ic = industryConfig[ind.value];
                const selected = industry === ind.value;
                return (
                  <button
                    key={ind.value}
                    type="button"
                    onClick={() => {
                      setIndustry(ind.value);
                      setPreviewStep(0);
                      if (fieldErrors.industry) {
                        setFieldErrors(prev => ({ ...prev, industry: '' }));
                      }
                    }}
                    disabled={isLocked || readOnly}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition h-full text-center ${selected
                      ? 'border-primary bg-primary/10 shadow-sm'
                      : fieldErrors.industry
                        ? 'border-error/40 bg-error/5 hover:border-error/60'
                        : 'border-base-200 bg-base-100 hover:border-base-300 shadow-sm'
                      } ${isLocked || readOnly ? 'cursor-not-allowed opacity-60 bg-base-200' : ''}`}
                  >
                    <span className="text-xl">{ic.icon}</span>
                    <span className={`text-[10px] font-bold leading-tight ${selected ? 'text-primary' : 'text-base-content/70'}`}>
                      {ind.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {fieldErrors.industry && (
              <p className="mt-2 text-xs text-error font-semibold flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {fieldErrors.industry}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="workflow-description" className="block text-sm font-semibold text-base-content/70 mb-2">
              Workflow Description
            </label>

            <textarea
              id="workflow-description"
              value={description}
              onChange={e => {
                setDescription(e.target.value);
                if (fieldErrors.description) {
                  setFieldErrors(prev => ({ ...prev, description: validateTextField(e.target.value, 'Workflow description', { required: false, minLength: null, maxLength: FIELD_LIMITS.DESCRIPTION }) }));
                }
              }}
              onBlur={() => setFieldErrors(prev => ({ ...prev, description: validateTextField(description, 'Workflow description', { required: false, minLength: null, maxLength: FIELD_LIMITS.DESCRIPTION }) }))}
              onFocus={() => setPreviewStep(0)}
              disabled={isLocked || readOnly}
              rows={2}
              placeholder="This is the workflow description for internal use. It can be used to identify its purpose, focus and when it should be used when creating assignments."
              className={`${CLASS_INPUT_BASE} text-sm resize-none disabled:opacity-60 ${fieldErrors.description ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
            />
            {fieldErrors.description && (
              <p className="text-error text-xs mt-1 font-medium">{fieldErrors.description}</p>
            )}

            <p className="mt-2 text-xs text-base-content/50">
              For informational purposes only. This will not be shown in capture UI.
            </p>
          </div>

          {/* Customer Reference Label */}
          <div>
            <label htmlFor="workflow-customer-reference-label" className="block text-sm font-semibold text-base-content/70 mb-2">
              Customer Reference Label
            </label>

            <input
              id="workflow-customer-reference-label"
              type="text"
              value={customerReferenceLabel}
              onChange={e => {
                setCustomerReferenceLabel(e.target.value);
                if (fieldErrors.customerReferenceLabel) {
                  setFieldErrors(prev => ({
                    ...prev,
                    customerReferenceLabel: validateTextField(e.target.value, 'Customer reference label', { required: false, minLength: null, maxLength: FIELD_LIMITS.MAX_CUSTOMER_REFERENCE_LABEL_LENGTH })
                  }));
                }
              }}
              onBlur={() => setFieldErrors(prev => ({
                ...prev,
                customerReferenceLabel: validateTextField(customerReferenceLabel, 'Customer reference label', { required: false, minLength: null, maxLength: FIELD_LIMITS.MAX_CUSTOMER_REFERENCE_LABEL_LENGTH })
              }))}
              onFocus={() => setPreviewStep(0)}
              disabled={isLocked || readOnly}
              placeholder="Reference"
              className={`${CLASS_INPUT_BASE} text-sm disabled:opacity-60 transition-all ${fieldErrors.customerReferenceLabel ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
            />
            {fieldErrors.customerReferenceLabel && (
              <p className="text-error text-xs mt-1 font-medium">{fieldErrors.customerReferenceLabel}</p>
            )}
          </div>


          {/* Branding */}
          <div className="border border-base-300 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setBrandingOpen(o => !o)}
              className="w-full flex items-center justify-between px-5 py-4 bg-base-100 hover:bg-base-200 transition"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Palette size={14} className="text-primary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-base-content">Customize Branding</p>
                  <p className="text-xs text-base-content/50">Color scheme and logo link</p>
                </div>
              </div>
              <ChevronRight size={16} className={`text-base-content/30 transition-transform ${brandingOpen ? 'rotate-90' : ''}`} />
            </button>

          {brandingOpen && (
            <div className="px-5 pb-5 pt-5 border-t border-base-300 space-y-5 bg-base-200/50">

              {/* Info banner — branding is always inherited from sub-account settings */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-start gap-2">
                <Palette size={16} className="text-primary shrink-0 mt-0.5" />
                <p className="text-xs text-base-content/70 leading-relaxed">
                  Theme and logo are inherited from your {SUB_ACCOUNT_LABEL.singularLower} workflow branding settings and cannot be changed here.
                </p>
              </div>

              {/* Theme Selector — read-only, shows sub-account workflow theme */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold text-base-content/60 uppercase tracking-wider">Experience Theme</span>
                </div>

                <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2">
                  {DAISY_THEMES.map((theme) => {
                    const isActive = (branding.theme || 'light') === theme;
                    return (
                      <label
                        key={theme}
                        className={`relative flex items-center gap-2 p-1.5 rounded-xl transition-all duration-200 select-none border-2 cursor-not-allowed ${isActive ? CLASS_SELECTION_ACTIVE : CLASS_SELECTION_INACTIVE} opacity-80`}
                      >
                        <input
                          type="radio"
                          name="preview-theme-controller"
                          value={theme}
                          checked={isActive}
                          onChange={() => {}}
                          className="sr-only"
                          disabled
                        />

                        <div data-theme={theme} className="bg-base-100 grid grid-cols-2 gap-0.5 rounded-lg p-1 shadow-sm border border-base-content/5 w-7 h-7 shrink-0 place-items-center relative">
                          <div className="bg-base-content w-1 h-1 rounded-full" />
                          <div className="bg-primary w-1 h-1 rounded-full" />
                          <div className="bg-secondary w-1 h-1 rounded-full" />
                          <div className="bg-accent w-1 h-1 rounded-full" />

                          {isActive && (
                            <div className="absolute inset-0 bg-primary/10 rounded-lg flex items-center justify-center">
                              <svg className="w-3.5 h-3.5 text-primary drop-shadow-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>

                        <span className={`text-[11px] font-bold capitalize truncate leading-none ${isActive ? 'text-primary' : 'text-base-content/70'}`}>
                          {theme}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Logo — read-only, shows sub-account workflow logo or default placeholder */}
              <div>
                <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">Brand Logo</span>
                {branding.logoUrl ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={branding.logoUrl}
                      alt="Workflow logo"
                      className="h-16 w-16 object-contain rounded-lg border border-base-300 bg-base-100 p-1"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      onLoad={(e) => { e.target.style.display = 'block'; }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-3 bg-base-200 border border-base-300 rounded-xl">
                    <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                      <Palette size={16} className="text-base-content/30" />
                    </div>
                    <span className="text-xs text-base-content/40">Default logo — configure in {SUB_ACCOUNT_LABEL.singular} settings to override</span>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>

          {/* Recipient Messaging */}
          <div className="border border-base-300 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setRecipientMessagingOpen(o => !o)}
              className="w-full flex items-center justify-between px-5 py-4 bg-base-100 hover:bg-base-200 transition"
            >
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                  <MessageSquare size={14} className="text-primary" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-base-content">Recipient Messaging</p>
                  <p className="text-xs text-base-content/50">Welcome, instructions, and thank you screen text</p>
                </div>
              </div>
              <ChevronRight size={16} className={`text-base-content/30 transition-transform ${recipientMessagingOpen ? 'rotate-90' : ''}`} />
            </button>

            {recipientMessagingOpen && (
              <div className="px-5 pb-5 pt-5 border-t border-base-300 space-y-5 bg-base-200/50">

                {/* Welcome screen */}
                <div className="space-y-3">
                  <span className="block text-xs font-semibold text-base-content/60 uppercase tracking-wide">Welcome Screen</span>
                  <div>
                    <input
                      type="text"
                      value={branding.welcomeTitle}
                      onChange={e => { updateBranding('welcomeTitle', e.target.value); if (brandingErrors.welcomeTitle) { validateBrandingField('welcomeTitle', e.target.value, 'Welcome title', FIELD_LIMITS.DEFAULT_TEXT); } }}
                      onBlur={() => validateBrandingField('welcomeTitle', branding.welcomeTitle, 'Welcome title', FIELD_LIMITS.DEFAULT_TEXT)}
                      onFocus={() => setPreviewStep(0)}
                      disabled={readOnly}
                      placeholder={'Welcome, {name}'}
                      className={`${CLASS_BRANDING_INPUT_BASE} ${readOnly ? 'opacity-70' : ''} ${brandingErrors.welcomeTitle ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                    />
                    {brandingErrors.welcomeTitle && <p className="text-error text-xs mt-1 font-medium">{brandingErrors.welcomeTitle}</p>}
                    <p className="text-xs text-base-content/40 mt-1">Heading — leave blank to use default. Use <code className="bg-base-200 px-1.5 py-0.5 rounded text-primary font-mono text-[11px]">{'{name}'}</code> to display recipient name.</p>
                  </div>
                  <div>
                    <textarea
                      value={branding.welcomeSubtitle}
                      onChange={e => { updateBranding('welcomeSubtitle', e.target.value); if (brandingErrors.welcomeSubtitle) { validateBrandingField('welcomeSubtitle', e.target.value, 'Welcome subtitle', FIELD_LIMITS.DESCRIPTION); } }}
                      onBlur={() => validateBrandingField('welcomeSubtitle', branding.welcomeSubtitle, 'Welcome subtitle', FIELD_LIMITS.DESCRIPTION)}
                      onFocus={() => setPreviewStep(0)}
                      disabled={readOnly}
                      rows={2}
                      placeholder={'Creating a claim can be stressful but we’re going to walk you through every step to help you get things moving along quickly and efficiently. - {creator_name}'}
                      className={`${CLASS_BRANDING_INPUT_BASE} resize-none ${readOnly ? 'opacity-70' : ''} ${brandingErrors.welcomeSubtitle ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                    />
                    {brandingErrors.welcomeSubtitle && <p className="text-error text-xs mt-1 font-medium">{brandingErrors.welcomeSubtitle}</p>}
                    <p className="text-xs text-base-content/40 mt-1">Subtitle — leave blank to use default. Use <code className="bg-base-200 px-1.5 py-0.5 rounded text-primary font-mono text-[11px]">{'{creator_name}'}</code> to display creator name.</p>
                  </div>
                </div>

                {/* Instructions screen */}
                <div className="space-y-3">
                  <span className="block text-xs font-semibold text-base-content/60 uppercase tracking-wide mb-2">Instructions Screen</span>
                  <div>
                    <input
                      type="text"
                      value={branding.instructionsMainTitle}
                      onChange={e => { updateBranding('instructionsMainTitle', e.target.value); if (brandingErrors.instructionsMainTitle) { validateBrandingField('instructionsMainTitle', e.target.value, 'Instructions main title', FIELD_LIMITS.DEFAULT_TEXT); } }}
                      onBlur={() => validateBrandingField('instructionsMainTitle', branding.instructionsMainTitle, 'Instructions main title', FIELD_LIMITS.DEFAULT_TEXT)}
                      onFocus={() => setPreviewStep(1)}
                      disabled={readOnly}
                      placeholder="What you'll need to capture"
                      className={`${CLASS_BRANDING_INPUT_BASE} ${readOnly ? 'opacity-70' : ''} ${brandingErrors.instructionsMainTitle ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                    />
                    {brandingErrors.instructionsMainTitle && <p className="text-error text-xs mt-1 font-medium">{brandingErrors.instructionsMainTitle}</p>}
                    <p className="text-xs text-base-content/40 mt-1">Heading — leave blank to use default</p>
                  </div>
                  <div>
                    <textarea
                      value={branding.instructionsHeading}
                      onChange={e => { updateBranding('instructionsHeading', e.target.value); if (brandingErrors.instructionsHeading) { validateBrandingField('instructionsHeading', e.target.value, 'Instructions heading', FIELD_LIMITS.DESCRIPTION); } }}
                      onBlur={() => validateBrandingField('instructionsHeading', branding.instructionsHeading, 'Instructions heading', FIELD_LIMITS.DESCRIPTION)}
                      onFocus={() => setPreviewStep(1)}
                      disabled={readOnly}
                      placeholder="Follow each step. You'll be able to review all media before final submission."
                      className={`${CLASS_BRANDING_INPUT_BASE} ${readOnly ? 'opacity-70' : ''} ${brandingErrors.instructionsHeading ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                    />
                    {brandingErrors.instructionsHeading && <p className="text-error text-xs mt-1 font-medium">{brandingErrors.instructionsHeading}</p>}
                    <p className="text-xs text-base-content/40 mt-1">Subtitle — leave blank to use description</p>
                  </div>
                </div>

                {/* Thank you screen */}
                <div className="space-y-3">
                  <span className="block text-xs font-semibold text-base-content/60 uppercase tracking-wide">Thank You Screen</span>
                  <div>
                    <input
                      type="text"
                      value={branding.thankyouTitle}
                      onChange={e => { updateBranding('thankyouTitle', e.target.value); if (brandingErrors.thankyouTitle) { validateBrandingField('thankyouTitle', e.target.value, 'Thank you title', FIELD_LIMITS.DEFAULT_TEXT); } }}
                      onBlur={() => validateBrandingField('thankyouTitle', branding.thankyouTitle, 'Thank you title', FIELD_LIMITS.DEFAULT_TEXT)}
                      onFocus={() => setPreviewStep(items.length + 2)}
                      disabled={readOnly}
                      placeholder="Media submitted!"
                      className={`${CLASS_BRANDING_INPUT_BASE} ${readOnly ? 'opacity-70' : ''} ${brandingErrors.thankyouTitle ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                    />
                    {brandingErrors.thankyouTitle && <p className="text-error text-xs mt-1 font-medium">{brandingErrors.thankyouTitle}</p>}
                    <p className="text-xs text-base-content/40 mt-1">Heading — leave blank to use default. Use <code className="bg-base-200 px-1.5 py-0.5 rounded text-primary font-mono text-[11px]">{'{name}'}</code> to display recipient name.</p>
                  </div>
                  <div>
                    <textarea
                      value={branding.thankyouMessage}
                      onChange={e => { updateBranding('thankyouMessage', e.target.value); if (brandingErrors.thankyouMessage) { validateBrandingField('thankyouMessage', e.target.value, 'Thank you message', FIELD_LIMITS.DESCRIPTION); } }}
                      onBlur={() => validateBrandingField('thankyouMessage', branding.thankyouMessage, 'Thank you message', FIELD_LIMITS.DESCRIPTION)}
                      onFocus={() => setPreviewStep(items.length + 2)}
                      disabled={readOnly}
                      rows={2}
                      placeholder="Your items have been securely sent."
                      className={`${CLASS_BRANDING_INPUT_BASE} resize-none ${readOnly ? 'opacity-70' : ''} ${brandingErrors.thankyouMessage ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                    />
                    {brandingErrors.thankyouMessage && <p className="text-error text-xs mt-1 font-medium">{brandingErrors.thankyouMessage}</p>}
                    <p className="text-xs text-base-content/40 mt-1">Body text — leave blank to use default</p>
                  </div>
                  <div>
                    <textarea
                      value={branding.whatHappensNextDescription}
                      onChange={e => { updateBranding('whatHappensNextDescription', e.target.value); if (brandingErrors.whatHappensNextDescription) { validateBrandingField('whatHappensNextDescription', e.target.value, 'What happens next description', FIELD_LIMITS.DESCRIPTION); } }}
                      onBlur={() => validateBrandingField('whatHappensNextDescription', branding.whatHappensNextDescription, 'What happens next description', FIELD_LIMITS.DESCRIPTION)}
                      onFocus={() => setPreviewStep(items.length + 2)}
                      disabled={readOnly}
                      rows={2}
                      placeholder="Your submitted imagery will be reviewed. You will receive updates once the process is complete."
                      className={`${CLASS_BRANDING_INPUT_BASE} resize-none ${readOnly ? 'opacity-70' : ''} ${brandingErrors.whatHappensNextDescription ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                    />
                    {brandingErrors.whatHappensNextDescription && <p className="text-error text-xs mt-1 font-medium">{brandingErrors.whatHappensNextDescription}</p>}
                    <p className="text-xs text-base-content/40 mt-1">What happens next description — leave blank to use default</p>
                  </div>
                </div>

              </div>
            )}
          </div>
          {/* Link Expiry */}
          <div className="border border-base-300 rounded-xl overflow-hidden shadow-sm">
            <div className="px-5 py-4 bg-base-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Clock size={14} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-base-content">Default Link Expiry</p>
                  <p className="text-xs text-base-content/50">Baseline expiry for assignments created with this template</p>
                </div>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="8760"
                    value={customValue}
                    onWheel={(e) => e.target.blur()}
                    onChange={e => {
                      setCustomValue(e.target.value);
                      validateExpiry(e.target.value, customUnit);
                    }}
                    onFocus={() => setPreviewStep(0)}
                    placeholder={loadingSettings ? "Loading..." : "Enter value"}
                    disabled={loadingSettings || isLocked || readOnly}
                    className={`w-24 px-3 py-2.5 bg-base-100 border ${CLASS_BORDER_DEFAULT} rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none disabled:opacity-50 transition-all ${readOnly && CLASS_READONLY_DISABLED}`}
                  />
                  <select
                    value={customUnit}
                    onChange={e => {
                      setCustomUnit(e.target.value);
                      setPreviewStep(0);
                      validateExpiry(customValue, e.target.value);
                    }}
                    disabled={loadingSettings || isLocked || readOnly}
                    className={`px-3 py-2.5 flex w-24 bg-base-100 border ${CLASS_BORDER_DEFAULT} rounded-xl text-sm font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none disabled:opacity-50 transition-all cursor-pointer ${readOnly && CLASS_READONLY_DISABLED}`}
                  >
                    {TIME_UNITS.map(unit => (
                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                    ))}
                  </select>
                </div>
                {customValue && parseInt(customValue, 10) > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 border border-primary/10 rounded-xl transition-all animate-in fade-in zoom-in duration-300">
                    <span className="text-[14px] font-black text-base-content/30 uppercase">≈</span>
                    <span className="text-[10px] text-primary font-black uppercase tracking-wider">
                      {formatExpiryLabel(parseInt(customValue, 10), customUnit)}
                    </span>
                  </div>
                )}
              </div>
              {fieldErrors.expiry && (
                <p className="mt-2 text-xs text-error font-semibold flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                  {fieldErrors.expiry}
                </p>
              )}

              {customValue && parseInt(customValue, 10) > 0 && (
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-base-content/5">
                  <div className="w-5 h-5 bg-success/10 rounded-md flex items-center justify-center">
                    <CheckCircle size={10} className="text-success" />
                  </div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                    Estimated Expiry: <span className="text-base-content/60">
                      {new Date(Date.now() + convertToHours(parseInt(customValue, 10), customUnit) * 3600000).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </p>
                </div>
              )}

            </div>
          </div>

          {/* Provenance Capture */}
          <div className="border border-base-300 rounded-xl overflow-hidden">
            <div className="px-5 py-4 bg-base-100">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Clock size={14} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-base-content">Provenance Capture</p>
                    <p className="text-xs text-base-content/50">Track metadata for this workflow</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => !readOnly && !loadingSettings && setCaptureProvenanceDetails(!captureProvenanceDetails)}
                  disabled={loadingSettings || readOnly}
                  className={`${CLASS_TOGGLE_TRACK} ${captureProvenanceDetails ? 'bg-primary' : 'bg-base-300/80 hover:bg-base-300'} ${(loadingSettings || readOnly) ? 'cursor-default ' : 'cursor-pointer'}`}
                >
                  <span
                    className={`${CLASS_TOGGLE_THUMB} ${captureProvenanceDetails ? 'translate-x-3.5' : 'translate-x-0'}`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Capture Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-sm font-semibold text-base-content/70">Capture Steps *</span>
                <p className="text-xs text-base-content/40 mt-0.5">{items.length} step{items.length !== 1 ? 's' : ''} added</p>
              </div>
            </div>

            <div className="space-y-3">
              {items.length === 0 && (
                <div className="text-center py-10 border-2 border-dashed border-base-300 rounded-xl">
                  <div className="text-3xl mb-2">📸</div>
                  <p className="text-sm font-medium text-base-content/50">No steps yet</p>
                  <p className="text-xs text-base-content/30 mt-0.5">Click "Add Step" to build your workflow</p>
                </div>
              )}

              {items.map((item, index) => {
                const tc = typeConfig[item.type] || typeConfig.photo;
                return (
                  <div
                    key={item.tempId}
                    className="border border-base-300 rounded-xl overflow-hidden bg-base-100 shadow-sm"
                    onClick={() => setPreviewStep(2 + index)}
                  >
                    {/* Card header */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 bg-base-200 border-b border-base-300 cursor-pointer">
                      <div className="flex flex-col gap-0.5">
                        <button type="button" onClick={e => { e.stopPropagation(); moveItem(item.tempId, -1); setPreviewStep(2 + index - 1); }} disabled={index === 0 || isLocked || readOnly} className="text-base-content opacity-40 hover:opacity-100 disabled:opacity-10">
                          <ChevronUp size={14} />
                        </button>
                        <button type="button" onClick={e => { e.stopPropagation(); moveItem(item.tempId, 1); setPreviewStep(2 + index + 1); }} disabled={index === items.length - 1 || isLocked || readOnly} className="text-base-content opacity-40 hover:opacity-100 disabled:opacity-10">
                          <ChevronDown size={14} />
                        </button>
                      </div>
                      <div className={`w-7 h-7 ${tc.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <tc.Icon size={14} className="text-white" />
                      </div>
                      <span className="text-xs font-bold flex-shrink-0 text-base-content">Step {index + 1}</span>
                      <div className="flex gap-1 ml-1">
                        {Object.entries(typeConfig).map(([type, conf]) => (
                          <button
                            key={type}
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              if (!isLocked && !readOnly) {
                                const updates = { type };
                                if (type === 'file') {
                                  updates.allowedFileType = null;
                                  updates.identifier_format = null;
                                } else if (item.type === 'file') {
                                  updates.allowedFileType = null;
                                }
                                if (type !== 'identifier') {
                                  updates.identifier_format = null;
                                }
                                setItems(prev => prev.map(i => i.tempId === item.tempId ? { ...i, ...updates } : i));
                              }
                              setPreviewStep(2 + index);
                            }}
                            disabled={isLocked || readOnly}
                            className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${item.type === type
                              ? `${conf.bg} text-white border-transparent shadow-sm scale-[1.05] ${readOnly ? 'opacity-80' : ''}`
                              : (readOnly
                                ? 'bg-transparent border-transparent text-base-content/30'
                                : 'bg-base-100 border-base-300 text-base-content/50 hover:border-base-content/30 hover:text-base-content')
                              } ${readOnly ? 'cursor-not-allowed' : ''}`}
                          >
                            {conf.label}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={e => { e.stopPropagation(); removeItem(item.tempId); }}
                        disabled={isLocked || readOnly}
                        className="ml-auto p-1 rounded-lg hover:bg-error/10 text-base-content/30 hover:text-error transition disabled:opacity-0"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {/* Card body */}
                    <div className="px-4 pt-3 pb-8 space-y-3">
                      <div className="w-full">
                        <input
                          type="text"
                          value={item.description}
                          autoFocus={item.tempId === lastAddedId}
                          onChange={e => {
                            e.stopPropagation();
                            updateItem(item.tempId, 'description', e.target.value);
                            if (fieldErrors.items[item.tempId]?.description) {
                              validateItem(item.tempId, 'description', e.target.value, item.type);
                            }
                          }}
                          onBlur={() => validateItem(item.tempId, 'description', item.description, item.type)}
                          onFocus={() => setPreviewStep(2 + index)}
                          disabled={isLocked || readOnly}
                          onClick={e => e.stopPropagation()}
                          placeholder={item.type === 'identifier' ? "e.g. Enter the vehicle VIN number" : item.type === 'video' ? "e.g. Record a 360° walk-around video" : item.type === 'file' ? "e.g. Upload document" : "e.g. Take a photo of the front bumper"}
                          className={`${CLASS_BRANDING_INPUT_BASE} transition-all ${fieldErrors.items[item.tempId]?.description ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                        />
                        {fieldErrors.items[item.tempId]?.description && (
                          <p className="text-error text-xs mt-1 font-medium">
                            {fieldErrors.items[item.tempId].description}
                          </p>
                        )}
                      </div>

                      <div className="flex items-start gap-3 relative">
                        {item.type === 'identifier' && (
                          <div className="flex-1 flex flex-col gap-1">
                            <select
                              value={item.identifierFormat || ''}
                              onChange={e => {
                                e.stopPropagation();
                                updateItem(item.tempId, 'identifierFormat', e.target.value || null);
                                validateItem(item.tempId, 'identifierFormat', e.target.value || null, item.type);
                              }}
                              onBlur={() => validateItem(item.tempId, 'identifierFormat', item.identifierFormat, item.type)}
                              onFocus={() => setPreviewStep(2 + index)}
                              onClick={e => e.stopPropagation()}
                              disabled={readOnly}
                              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-base-100 text-base-content transition-all ${readOnly ? 'opacity-70' : ''} ${fieldErrors.items[item.tempId]?.identifierFormat ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT} ${readOnly && CLASS_READONLY_DISABLED}`}
                            >
                              <option value="">Select format *</option>
                              {IDENTIFIER_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                            {fieldErrors.items[item.tempId]?.identifierFormat && (
                              <p className="text-error mt-6 text-[11px] font-semibold flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                                {fieldErrors.items[item.tempId].identifierFormat}
                              </p>
                            )}
                          </div>
                        )}

                        {item.type === 'file' && (
                          <div className="flex-1 flex flex-col gap-1">
                            {readOnly ? (
                              <div className={`${CLASS_BRANDING_INPUT_BASE} ${CLASS_READONLY_DISABLED} opacity-70`}>
                                {item.allowedFileType ? item.allowedFileType.toUpperCase() : <span className="text-base-content/30">No format selected</span>}
                              </div>
                            ) : (
                              <select
                                value={item.allowedFileType || ''}
                                onChange={e => {
                                  e.stopPropagation();
                                  updateItem(item.tempId, 'allowedFileType', e.target.value || null);
                                  if (fieldErrors.items[item.tempId]?.allowedFileType) {
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      items: {
                                        ...prev.items,
                                        [item.tempId]: { ...(prev.items[item.tempId] || {}), allowedFileType: '' }
                                      }
                                    }));
                                  }
                                }}
                                onFocus={() => setPreviewStep(2 + index)}
                                onClick={e => e.stopPropagation()}
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 z-50 focus:ring-primary focus:border-primary outline-none bg-base-100 text-base-content transition-all ${fieldErrors.items[item.tempId]?.allowedFileType ? CLASS_INPUT_ERROR : CLASS_BORDER_DEFAULT}`}
                              >
                                <option value="">Select file format *</option>
                                {SUPPORTED_FILE_FORMATS.map(f => (
                                  <option key={f.ext} value={f.ext}>{f.label}</option>
                                ))}
                              </select>
                            )}
                            {fieldErrors.items[item.tempId]?.allowedFileType && (
                              <p className="text-error text-[11px] font-semibold flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                                {fieldErrors.items[item.tempId].allowedFileType}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-6 ml-auto">
                          {item.type === 'video' && (
                            <div className="flex items-center gap-2.5">
                              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40 select-none">Audio</span>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); !readOnly && updateItem(item.tempId, 'audioRequired', !item.audioRequired); setPreviewStep(2 + index); }}
                                className={`${CLASS_TOGGLE_TRACK} ${item.audioRequired ? 'bg-primary' : 'bg-base-300/80 hover:bg-base-300'} ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                              >
                                <span
                                  className={`${CLASS_TOGGLE_THUMB} ${item.audioRequired ? 'translate-x-3.5' : 'translate-x-0'}`}
                                />
                              </button>
                            </div>
                          )}
                          <div>
                            <span
                              className="flex items-center gap-2.5 text-[10px] font-black uppercase tracking-widest text-base-content/40 cursor-pointer"
                              onClick={e => e.stopPropagation()}
                            >
                              <span>Required</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (readOnly) return;
                                  const requiredCount = items.filter(i => i.required).length;
                                  if (item.required && requiredCount === 1) {
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      items: {
                                        ...prev.items,
                                        [item.tempId]: {
                                          ...(prev.items[item.tempId] || {}),
                                          required: 'At least one step must be required'
                                        }
                                      }
                                    }));
                                    return;
                                  }
                                  updateItem(item.tempId, 'required', !item.required);
                                  setPreviewStep(2 + index);
                                  if (fieldErrors.items[item.tempId]?.required) {
                                    setFieldErrors(prev => ({
                                      ...prev,
                                      items: {
                                        ...prev.items,
                                        [item.tempId]: {
                                          ...(prev.items[item.tempId] || {}),
                                          required: ''
                                        }
                                      }
                                    }));
                                  }
                                }}
                                className={`${CLASS_TOGGLE_TRACK} ${item.required ? 'bg-primary' : 'bg-base-300/80 hover:bg-base-300'} ${readOnly ? 'cursor-default ' : 'cursor-pointer'}`}
                              >
                                <span
                                  className={`${CLASS_TOGGLE_THUMB} ${item.required ? 'translate-x-3.5' : 'translate-x-0'}`}
                                />
                              </button>
                            </span>
                            {fieldErrors.items[item.tempId]?.required && (
                              <p className={`absolute top-full right-0 text-error text-xs font-medium  whitespace-nowrap animate-in fade-in slide-in-from-top-1 ${item.type === "identifier" ? 'mt-6' : 'mt-1'}`}>
                                {fieldErrors.items[item.tempId].required}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {fieldErrors.steps && items.length === 0 && (
                <p className="mt-2 text-xs text-error font-semibold flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                  {fieldErrors.steps}
                </p>
              )}

              {/* Add Step Button - positioned at bottom */}
              <div className="pt-3 flex justify-end">
                <button
                  type="button"
                  onClick={addItem}
                  disabled={isLocked || readOnly}
                  className={`btn btn-primary btn-sm flex items-center gap-1.5 px-4 h-10 rounded-xl ${readOnly ? 'hidden' : ''}`}
                >
                  <Plus size={15} /> Add Step
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-xl text-sm font-bold">{error}</div>
          )}

          {!readOnly && (
            <div className="flex gap-3 pb-6">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 py-3 border border-base-300 text-base-content/70 bg-base-100 text-[14px] rounded-xl font-medium hover:bg-base-200 transition text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || isLocked}
                className="flex-1 btn btn-primary !h-12 !px-0 rounded-xl font-black uppercase tracking-widest text-sm disabled:opacity-50"
              >
                {saving ? 'Saving...' : workflow ? 'Save Changes' : 'Create Workflow'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right: Live Preview */}
      <div className="w-full lg:w-[350px] xl:w-[380px] flex-shrink-0 bg-base-100 flex flex-col border-t lg:border-t-0 lg:border-l border-base-300 transition-all duration-500 overflow-hidden">
        {/* Sidebar Header */}
        <div className="px-6 py-5 border-b border-base-200 bg-base-100/80 backdrop-blur-md sticky top-0 z-20 flex justify-between mt-2">
          <p className="text-[12px] font-black text-base-content uppercase tracking-[0.15em]">Live Preview</p>
          <p className="text-[10px] font-bold text-base-content/40 uppercase tracking-widest">Interactive device mirror</p>
        </div>

        {/* Screen selector - Pill layout */}
        <div className="px-3 py-3 bg-base-200 border-b border-base-200 flex-shrink-0">
          <div className="flex flex-wrap gap-1 justify-center">
            {screenLabels.map((label, i) => (
              <button
                key={i}
                onClick={() => setPreviewStep(i)}
                className={`flex-shrink-0 px-2 py-1 rounded-full text-[8.5px] font-black transition-all uppercase tracking-widest border ${previewStep === i
                  ? 'text-white bg-[#42A5F5] border-[#42A5F5] shadow-[0_4px_12px_rgba(66,165,245,0.25)] scale-105'
                  : 'bg-base-100 border-base-300 text-base-content/40 hover:border-base-content/30 hover:text-base-content/60'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Device Mirror Area */}
        <div className="flex-1 flex flex-col items-center p-3 sm:p-5 bg-base-200/50 relative overflow-y-auto">
          {/* Subtle background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#42A5F5]/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="relative z-10 transition-transform duration-500 hover:scale-[1.02]">
            <PhonePreview
              workflowName={name}
              name={branding.welcomeTitle}
              submitterName=""
              industry={industry}
              description={description}
              items={items}
              previewStep={previewStep}
              branding={branding}
              expiresAt={expiresAtDate}
              customerReferenceLabel={customerReferenceLabel}
            />
          </div>
        </div>

        {/* Navigation - Bottom bar */}
        <div className="px-6 py-4 bg-base-100 border-t border-base-200 flex items-center justify-between flex-shrink-0">
          <button
            onClick={() => setPreviewStep(p => Math.max(0, p - 1))}
            disabled={previewStep === 0}
            className="w-10 h-10 rounded-full border border-base-300 flex items-center justify-center hover:bg-base-200 disabled:opacity-20 text-base-content/40 transition-all active:scale-90"
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
          </button>

          <div className="flex flex-col items-center">
            <span className="text-[11px] font-black text-base-content tracking-[0.3em] uppercase">{previewStep + 1} / {Math.max(1, totalScreens)}</span>
            <div className="w-12 h-[2px] bg-[#42A5F5] rounded-full mt-1.5 opacity-20" />
          </div>

          <button
            onClick={() => setPreviewStep(p => Math.min(totalScreens - 1, p + 1))}
            disabled={previewStep >= totalScreens - 1}
            className="w-10 h-10 rounded-full border border-base-300 flex items-center justify-center hover:bg-base-200 disabled:opacity-20 text-base-content/40 transition-all active:scale-90"
          >
            <ChevronRight size={20} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowBuilder;

WorkflowBuilder.propTypes = {
  readOnly: PropTypes.bool,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  workflow: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    industry: PropTypes.string,
    description: PropTypes.string,
    customerReferenceLabel: PropTypes.string,
    inUse: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        type: PropTypes.string,
        description: PropTypes.string,
        required: PropTypes.bool,
        identifierFormat: PropTypes.string,
        stepOrder: PropTypes.number,
        audioRequired: PropTypes.bool,
        allowedFileType: PropTypes.string,
      })
    ),
    branding: PropTypes.shape({
      theme: PropTypes.string,
      logoUrl: PropTypes.string,
      welcomeTitle: PropTypes.string,
      welcomeSubtitle: PropTypes.string,
      instructionsMainTitle: PropTypes.string,
      instructionsHeading: PropTypes.string,
      thankyouTitle: PropTypes.string,
      thankyouMessage: PropTypes.string,
      whatHappensNextDescription: PropTypes.string,
    }),
    defaultExpiry: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expiryTimeUnit: PropTypes.string,
    defaultExpiryHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    captureProvenanceDetails: PropTypes.bool,
  }),
};
